import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Loader2, Pencil, Presentation, Trash2, Briefcase, X, Mail, ChevronLeft, ChevronRight, ExternalLink, FolderOpen } from 'lucide-react';
import { useApi } from '../lib/api';
import { getStatusBadgeStyle, formatDateShort, formatCurrency } from '../lib/utils';
import { OpportunityModal } from '../components/OpportunityModal';
import { GenerationProgress } from '../components/GenerationProgress';
import { useActiveRuns } from '../lib/activeRuns';
import { ViewsDropdown } from '../components/ViewsDropdown';
import type { ApiResponse, Opportunity, OpportunityView, PicklistItem, PicklistCategory, User } from '../types';

const EMPTY_FILTERS = {
    client: '',
    name: '',
    statusId: '',
    practiceId: '',
    ownerId: '',
    consultantId: '',
    createdFrom: '',
};

type Filters = typeof EMPTY_FILTERS;

function usePicklist(category: PicklistCategory) {
    const api = useApi();
    return useQuery({
        queryKey: ['picklists', category],
        queryFn: () => api.get<ApiResponse<PicklistItem[]>>(`/api/picklists/${category}`),
    });
}

export function Opportunities() {
    const api = useApi();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;
    const [editing, setEditing] = useState<Opportunity | null | undefined>(undefined);
    const [hoverInfo, setHoverInfo] = useState<{ opportunity: Opportunity; x: number; y: number } | null>(null);
    const hoverClearTimeout = useRef<number | null>(null);

    // Petit délai de grâce avant de fermer l'infobulle : sans ça, passer la
    // souris de la ligne vers l'infobulle (pour cliquer sur l'email du
    // contact) la ferme aussitôt puisqu'on quitte la ligne au passage.
    const cancelHoverClear = () => {
        if (hoverClearTimeout.current) {
            window.clearTimeout(hoverClearTimeout.current);
            hoverClearTimeout.current = null;
        }
    };
    const scheduleHoverClear = () => {
        hoverClearTimeout.current = window.setTimeout(() => setHoverInfo(null), 150);
    };
    // Déclenche l'infobulle sur les cellules Client et Nom uniquement — pas sur
    // toute la ligne, pour rester discret au survol du reste du tableau.
    const hoverHandlers = (o: Opportunity) => ({
        onMouseEnter: (e: MouseEvent) => { cancelHoverClear(); setHoverInfo({ opportunity: o, x: e.clientX, y: e.clientY }); },
        onMouseMove: (e: MouseEvent) => setHoverInfo((h) => (h && h.opportunity.id === o.id ? { ...h, x: e.clientX, y: e.clientY } : h)),
        onMouseLeave: scheduleHoverClear,
    });

    const { data: statuses } = usePicklist('status');
    const { data: practices } = usePicklist('practice');
    // Owner/Consultant : personnes déjà connectées à l'outil (table users).
    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/users'),
    });

    const queryParams = { q: search || undefined, ...Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v)
    ) };

    const { data, isLoading } = useQuery({
        queryKey: ['opportunities', search, filters, page],
        queryFn: () => api.get<ApiResponse<Opportunity[]> & { count: number; page: number; pageSize: number }>(
            '/api/opportunities', { ...queryParams, page, pageSize: PAGE_SIZE }
        ),
    });
    const totalCount = data?.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    // Générations en cours, toutes avant-ventes confondues : la liste les
    // affiche pour qu'on n'ait pas à rouvrir la fenêtre des propositions, ni à
    // la laisser ouverte pendant que la génération se déroule.
    const generations = useActiveRuns();

    // Repart de la première page à chaque changement de recherche/filtres —
    // rester sur la page 3 d'un nouveau filtrage n'aurait pas de sens.
    useEffect(() => { setPage(1); }, [search, filters]);

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/opportunities/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunities'] }),
        onError: (err: any) => alert(err.message || 'La suppression a échoué'),
    });

    // Vues enregistrées (combinaisons de filtres nommées, propres à l'utilisateur).
    const { data: viewsData } = useQuery({
        queryKey: ['opportunity-views'],
        queryFn: () => api.get<ApiResponse<OpportunityView[]>>('/api/opportunity-views'),
    });
    const views = viewsData?.data || [];

    const applyView = (view: OpportunityView) => {
        const { q, ...rest } = view.filters;
        setSearch(q || '');
        setFilters({ ...EMPTY_FILTERS, ...rest });
    };

    // Charge automatiquement la vue favorite (= vue par défaut) une seule fois,
    // à l'arrivée sur la page — sans écraser un filtrage déjà en cours si les
    // vues se rechargent ensuite (ex: après une mutation).
    const appliedDefaultView = useRef(false);
    useEffect(() => {
        if (appliedDefaultView.current || !viewsData) return;
        appliedDefaultView.current = true;
        const favorite = views.find((v) => v.isFavorite);
        if (favorite) applyView(favorite);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewsData]);

    const createViewMutation = useMutation({
        mutationFn: (name: string) => {
            const viewFilters = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), ...(search ? { q: search } : {}) };
            return api.post<ApiResponse<OpportunityView>>('/api/opportunity-views', { name, filters: viewFilters });
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunity-views'] }),
    });

    const deleteViewMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/opportunity-views/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunity-views'] }),
    });

    const toggleFavoriteViewMutation = useMutation({
        mutationFn: (view: OpportunityView) => view.isFavorite
            ? api.delete(`/api/opportunity-views/${view.id}/favorite`)
            : api.post(`/api/opportunity-views/${view.id}/favorite`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunity-views'] }),
    });

    const opportunities = data?.data || [];
    const hasActiveFilters = Object.values(filters).some(Boolean) || !!search;

    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch(''); };

    const setFilter = (key: keyof Filters, value: string) => setFilters((f) => ({ ...f, [key]: value }));

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">Avant-ventes</h1>
                    <p className="text-[var(--color-text-secondary)]">
                        {totalCount} avant-vente{totalCount > 1 ? 's' : ''}
                    </p>
                </div>
                <button onClick={() => setEditing(null)} className="btn-primary">
                    <Plus className="w-4 h-4" />
                    Nouvelle avant-vente
                </button>
            </div>

            <div className="space-y-3">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher (client, nom, status, practice, owner, consultant...)"
                            className="input pl-10"
                        />
                    </div>
                    <ViewsDropdown
                        views={views}
                        onApply={applyView}
                        onSave={(name) => createViewMutation.mutate(name)}
                        onToggleFavorite={(view) => toggleFavoriteViewMutation.mutate(view)}
                        onDelete={(id) => deleteViewMutation.mutate(id)}
                        isSaving={createViewMutation.isPending}
                    />
                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="btn-secondary">
                            <X className="w-4 h-4" />
                            Effacer les filtres
                        </button>
                    )}
                </div>

                <div className="card grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div>
                        <label className="field-label">Client</label>
                        <input className="input" value={filters.client} onChange={(e) => setFilter('client', e.target.value)} />
                    </div>
                    <div>
                        <label className="field-label">Nom</label>
                        <input className="input" value={filters.name} onChange={(e) => setFilter('name', e.target.value)} />
                    </div>
                    <div>
                        <label className="field-label">Status</label>
                        <select className="input" value={filters.statusId} onChange={(e) => setFilter('statusId', e.target.value)}>
                            <option value="">Tous</option>
                            {(statuses?.data || []).map((s) => <option key={s.id} value={s.id}>{s.value}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Practice</label>
                        <select className="input" value={filters.practiceId} onChange={(e) => setFilter('practiceId', e.target.value)}>
                            <option value="">Toutes</option>
                            {(practices?.data || []).map((p) => <option key={p.id} value={p.id}>{p.value}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Owner</label>
                        <select className="input" value={filters.ownerId} onChange={(e) => setFilter('ownerId', e.target.value)}>
                            <option value="">Tous</option>
                            {(users?.data || []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Consultant</label>
                        <select className="input" value={filters.consultantId} onChange={(e) => setFilter('consultantId', e.target.value)}>
                            <option value="">Tous</option>
                            {(users?.data || []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Créée depuis</label>
                        <input type="date" className="input" value={filters.createdFrom} onChange={(e) => setFilter('createdFrom', e.target.value)} />
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : opportunities.length === 0 ? (
                <div className="text-center py-12 card">
                    <Briefcase className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
                    <p className="text-[var(--color-text-secondary)]">Aucune avant-vente trouvée</p>
                </div>
            ) : (
                <div className="card overflow-x-auto p-0">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-text-muted)]">
                                <th className="px-4 py-3">Client</th>
                                <th className="px-4 py-3">Nom</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Practice</th>
                                <th className="px-4 py-3">Owner</th>
                                <th className="px-4 py-3">Contact</th>
                                <th className="px-4 py-3">Consultant</th>
                                <th className="px-4 py-3">Jours estimés</th>
                                <th className="px-4 py-3">Estimation CA</th>
                                <th className="px-4 py-3">Restitution</th>
                                <th className="px-4 py-3">Créée le</th>
                                <th className="px-4 py-3 sticky right-0 bg-[var(--color-bg-card)] border-l border-[var(--color-border)]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {opportunities.map((o) => (
                                <tr
                                    key={o.id}
                                    className="group border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-bg-hover)] cursor-pointer"
                                    style={{ borderLeft: `4px solid ${o.statusColor || 'transparent'}` }}
                                    onDoubleClick={() => setEditing(o)}
                                >
                                    <td className="px-4 py-3 font-medium text-[var(--color-text)]" {...hoverHandlers(o)}>
                                        {o.client}
                                    </td>
                                    <td className="px-4 py-3" {...hoverHandlers(o)}>
                                        {o.name}
                                        {/* La génération se poursuit sans sa fenêtre : c'est
                                            ici qu'on voit qu'elle tourne encore. */}
                                        {generations.parAvantVente.get(o.id) && (
                                            <div className="mt-1">
                                                <GenerationProgress
                                                    run={generations.parAvantVente.get(o.id)!}
                                                    compact
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="badge" style={getStatusBadgeStyle(o.statusValue, o.statusOutcome, o.statusColor)}>{o.statusValue}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {o.practices.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {o.practices.map((p) => (
                                                    <span key={p.id} className="badge badge-default">{p.value}</span>
                                                ))}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{o.ownerValue}</td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                                        {o.contactName ? (
                                            <span className="inline-flex items-center gap-1.5 max-w-full">
                                                <span className="truncate">{o.contactName}</span>
                                                {o.contactEmail && (
                                                    <a
                                                        href={`mailto:${o.contactEmail}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="p-1 rounded-lg hover:bg-[var(--color-bg-hover)] flex-shrink-0"
                                                        title={`Écrire à ${o.contactEmail}`}
                                                    >
                                                        <Mail className="w-3.5 h-3.5 text-[var(--color-primary)]" />
                                                    </a>
                                                )}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                                        {o.consultants.length > 0 ? o.consultants.map((c) => c.displayName).join(', ') : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{o.estimatedDays ?? '-'}</td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{formatCurrency(o.estimatedRevenue)}</td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{formatDateShort(o.restitutionDate)}</td>
                                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">{formatDateShort(o.createdAt)}</td>
                                    <td className="px-4 py-3 sticky right-0 bg-[var(--color-bg-card)] group-hover:bg-[var(--color-bg-hover)] border-l border-[var(--color-border)]">
                                        <div className="flex items-center gap-1 justify-end">
                                            {o.teamsFolderUrl && (
                                                <a href={o.teamsFolderUrl} target="_blank" rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                                    title="Ouvrir le dossier Teams">
                                                    <FolderOpen className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                                </a>
                                            )}
                                            {o.crmOpportunityUrl && (
                                                <a href={o.crmOpportunityUrl} target="_blank" rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                                    title="Voir dans le CRM">
                                                    <ExternalLink className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                                </a>
                                            )}
                                            <Link
                                                to={`/opportunities/${o.id}/proposals`}
                                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                                title="Propositions commerciales">
                                                <Presentation className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                            </Link>
                                            <button onClick={() => setEditing(o)}
                                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                                title="Modifier">
                                                <Pencil className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (confirm(`Supprimer "${o.name}" ?`)) deleteMutation.mutate(o.id);
                                                }}
                                                className="p-1.5 rounded-lg hover:bg-[var(--color-error-light)]"
                                                title="Supprimer">
                                                <Trash2 className="w-4 h-4 text-[var(--color-error)]" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--color-border)]">
                            <p className="text-sm text-[var(--color-text-secondary)]">
                                Page {page} / {totalPages} — {totalCount} avant-vente{totalCount > 1 ? 's' : ''}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="btn-secondary px-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Page précédente"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    className="btn-secondary px-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Page suivante"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {hoverInfo && (
                <div
                    className="fixed z-50 card p-3 shadow-lg w-80 text-sm"
                    style={{
                        left: Math.min(hoverInfo.x + 16, window.innerWidth - 340),
                        top: Math.min(hoverInfo.y + 16, window.innerHeight - 360),
                    }}
                    onMouseEnter={cancelHoverClear}
                    onMouseLeave={scheduleHoverClear}
                >
                    <p className="font-semibold text-[var(--color-text)]">{hoverInfo.opportunity.client} — {hoverInfo.opportunity.name}</p>
                    {hoverInfo.opportunity.description && (
                        <p className="text-[var(--color-text-secondary)] mt-1 line-clamp-3">{hoverInfo.opportunity.description}</p>
                    )}
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
                        <dt className="text-[var(--color-text-muted)]">Status</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">{hoverInfo.opportunity.statusValue}</dd>
                        <dt className="text-[var(--color-text-muted)]">Owner</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">{hoverInfo.opportunity.ownerValue}</dd>
                        <dt className="text-[var(--color-text-muted)]">Contact</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">{hoverInfo.opportunity.contactName || '-'}</dd>
                        <dt className="text-[var(--color-text-muted)]">Practices</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">
                            {hoverInfo.opportunity.practices.length > 0 ? hoverInfo.opportunity.practices.map((p) => p.value).join(', ') : '-'}
                        </dd>
                        <dt className="text-[var(--color-text-muted)]">Consultants</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">
                            {hoverInfo.opportunity.consultants.length > 0 ? hoverInfo.opportunity.consultants.map((c) => c.displayName).join(', ') : '-'}
                        </dd>
                        <dt className="text-[var(--color-text-muted)]">Jours estimés</dt>
                        <dd className="text-[var(--color-text-secondary)]">{hoverInfo.opportunity.estimatedDays ?? '-'}</dd>
                        <dt className="text-[var(--color-text-muted)]">Estimation CA</dt>
                        <dd className="text-[var(--color-text-secondary)]">{formatCurrency(hoverInfo.opportunity.estimatedRevenue)}</dd>
                        <dt className="text-[var(--color-text-muted)]">Restitution</dt>
                        <dd className="text-[var(--color-text-secondary)]">{formatDateShort(hoverInfo.opportunity.restitutionDate)}</dd>
                        <dt className="text-[var(--color-text-muted)]">Créée le</dt>
                        <dd className="text-[var(--color-text-secondary)]">{formatDateShort(hoverInfo.opportunity.createdAt)}</dd>
                        <dt className="text-[var(--color-text-muted)]">Créée par</dt>
                        <dd className="text-[var(--color-text-secondary)] truncate">{hoverInfo.opportunity.createdByName || '-'}</dd>
                    </dl>
                </div>
            )}

            {editing !== undefined && (
                <OpportunityModal opportunity={editing} onClose={() => setEditing(undefined)} />
            )}

        </div>
    );
}
